import React from 'react';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import {
	Grid,
	IconButton,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography
} from '@mui/material';

import { AuthorizerContext } from 'app/providers/acl/AclProvider';
import {
	BooleanField,
	Icon,
	SearchSelectField,
	SimpleTextField,
	SquareChip,
	useSettings,
	useUserData,
	Utils
} from 'app/main/components';
import { DetailsSectionWrapper, type DetailsSectionInfoContentProps } from 'app/main/components/page-layouts';
import { showChangeLoginMailDialog } from './components/ChangeLoginMailDialog';
import UsersLoginLogsListDialog from './components/UsersLoginLogsListDialog';

export default function UsersDetailsSectionSystemInfo(props: Omit<DetailsSectionInfoContentProps, 'logProps'>) {
	const dispatch = useDispatch();
	const { t } = useSettings();
	const userData = useUserData();
	const intl = useIntl();
	const authorizer = React.useContext(AuthorizerContext);
	const [isOpenChronology, setIsOpenChronology] = React.useState(false);

	const openChronology = () => setIsOpenChronology(true);

	const systemInfoAuthorized = authorizer.can('update-systeminfo', 'users');

	function changeUserMail() {
		dispatch(showChangeLoginMailDialog({ userMail: props.model.user_mail, sendChange: props.sendChange }));
	}

	function changeUserEnabled(_event, value) {
		props.sendChange('user_enabled', value === 'enabled');
	}

	const lastLoginDate = Utils.parseDate(props.model.user_last_login)?.toDate();

	return (
		<DetailsSectionWrapper
			title={t('USERS_APPSETTINGS_SECTION')}
			subheader={t('USERS_APPSETTINGS_SUBHEADER')}
			disablePadding
		>
			<List className="pt-0" dense>
				{!props.isEdit && (
					<ListItem className="py-0" alignItems="flex-start">
						<ListItemIcon className="min-w-40">
							<Icon icon="login" />
						</ListItemIcon>
						<ListItemText
							primary={t('LAST_LOGIN')}
							secondary={
								lastLoginDate
									? intl.formatDate(lastLoginDate, {
											year: 'numeric',
											month: 'long',
											day: 'numeric',
											hour: '2-digit',
											minute: '2-digit'
										})
									: '-'
							}
						/>
						{authorizer.can('view', 'sys-logs') && (
							<ListItemIcon className="min-w-40">
								<Tooltip title={t('USER_LOGIN_LOGS')}>
									<IconButton onClick={openChronology}>
										<Icon icon="table-clock" />
									</IconButton>
								</Tooltip>
							</ListItemIcon>
						)}
					</ListItem>
				)}
				<ListItem
					className="py-0"
					alignItems="flex-start"
					secondaryAction={
						props.isEdit &&
						systemInfoAuthorized && (
							<Tooltip title={t('LOGIN_EMAIL_EDIT_BUTTON')} arrow>
								<IconButton size="small" onClick={changeUserMail}>
									<Icon icon="edit" />
								</IconButton>
							</Tooltip>
						)
					}
				>
					<ListItemIcon className="min-w-40">
						<Icon icon="email" />
					</ListItemIcon>
					<ListItemText primary={t('LOGIN_EMAIL')} secondary={props.model.user_mail} />
				</ListItem>
				<ListItem className="py-0" alignItems="flex-start">
					<ListItemIcon className="min-w-40">
						<Icon icon="account-cancel" />
					</ListItemIcon>
					<ListItemText
						slotProps={{ secondary: { component: 'div' } }}
						primary={t('DETAILS_USERS_STATUS_TITLE')}
						secondary={
							props.isEdit && systemInfoAuthorized && props.model.user_uid !== userData.user_uid ? (
								<ToggleButtonGroup
									className="mt-4 block"
									size="small"
									value={props.model.user_enabled ? 'enabled' : 'disabled'}
									onChange={changeUserEnabled}
									exclusive
								>
									<ToggleButton value="enabled" color="success">
										{t('DETAILS_USERS_STATUS_ACTIVE')}
									</ToggleButton>
									<ToggleButton value="disabled" color="error">
										{t('DETAILS_USERS_STATUS_DISABLED')}
									</ToggleButton>
								</ToggleButtonGroup>
							) : props.model.user_enabled ? (
								<SquareChip
									label={t('DETAILS_USERS_STATUS_ACTIVE')}
									backgroundColor={theme => theme.palette.success.main}
								/>
							) : (
								<SquareChip
									label={t('DETAILS_USERS_STATUS_DISABLED')}
									backgroundColor={theme => theme.palette.error.main}
								/>
							)
						}
					/>
				</ListItem>
				<ListItem className="py-0" alignItems="flex-start">
					<ListItemIcon className="min-w-40">
						<Icon icon="badge-account" />
					</ListItemIcon>
					<ListItemText
						slotProps={{ secondary: { component: 'div' } }}
						primary={t('ROLE')}
						secondary={
							props.isEdit && systemInfoAuthorized ? (
								<SearchSelectField
									className="pt-4"
									name="user_role"
									label={t('DETAILS_USERS_FIELD_ROLE')}
									systable="sys_roles"
									value={props.model.user_role}
									isEdit
									sendChange={props.sendChange}
								/>
							) : (
								<Typography color="text.secondary">{props.model.user_role_title}</Typography>
							)
						}
					/>
				</ListItem>
				{(systemInfoAuthorized || props.model.user_uid === userData.user_uid) && (
					<ListItem className="py-0" alignItems="flex-start">
						<ListItemIcon className="min-w-40">
							<Icon icon="lock" />
						</ListItemIcon>
						<ListItemText
							slotProps={{ secondary: { component: 'div' } }}
							primary={t('PASSWORD')}
							secondary={
								!props.isEdit ? (
									props.model.user_force_reset_password ? (
										<SquareChip
											label={t('DETAILS_USERS_FIELD_FORCE_RESET_PASSWORD')}
											icon={<Icon icon="alert-box" size={20} color="text.secondary" />}
										/>
									) : (
										<Typography color="text.secondary" fontStyle="italic">
											{t('DETAILS_USERS_CHANGE_PASSWORD_TEXT')}
										</Typography>
									)
								) : (
									<>
										<Grid container spacing={1} className="pt-4">
											<Grid size={12}>
												<SimpleTextField
													name="user_pass"
													label={t('DETAILS_USERS_FIELD_PASSWORD')}
													type="password"
													isEdit
													sendChange={() => {}}
													validationError={t('ERROR_TEXT_REQUIRED')}
												/>
											</Grid>
											<Grid size={12}>
												<SimpleTextField
													name="user_pass_confirm"
													label={t('DETAILS_USERS_FIELD_CONFIRM_PASSWORD')}
													type="password"
													isEdit
													sendChange={(_, value) => props.sendChange('user_pass', value)}
													validations={{ equalsField: 'user_pass' }}
													validationErrors={{
														isDefaultRequiredValue: t('ERROR_TEXT_REQUIRED'),
														equalsField: t('ERROR_PASSWORD_NO_MATCH')
													}}
												/>
											</Grid>
										</Grid>
										<BooleanField
											name="user_force_reset_password"
											label={t('DETAILS_USERS_FIELD_FORCE_RESET_PASSWORD')}
											value={props.model.user_force_reset_password}
											isEdit
											sendChange={props.sendChange}
										/>
									</>
								)
							}
						/>
					</ListItem>
				)}
			</List>
			<UsersLoginLogsListDialog
				open={isOpenChronology}
				onClose={() => setIsOpenChronology(false)}
				model={props.model}
				filters={[
					{ filt_field: 'logs_user_uid', filt_clause: 'equals', filt_value: props.model.user_uid },
					{ filt_field: 'logs_lgty_id', filt_clause: 'equals', filt_value: 4 }
				]}
			/>
		</DetailsSectionWrapper>
	);
}
